import { groupBy, maxBy } from "lodash";
import moment, { Moment } from "moment";
import { titleize } from "underscore.string";

import { formatMoney, getEarningAmount, valueIsDefined } from "lib/helpers";
import { earningSourceNameToDisplayName } from "services/valuation/constants";
import {
  EarningByRegion,
  Source,
  ValuationResult,
} from "services/valuation/types";

export function getEarningsSince(result: ValuationResult): string {
  return result.earnings_by_region[0][0].slice(0, 4);
}

export function getMostInSingleYear(
  result: ValuationResult,
): { year: number; totalAmount: number } {
  const yearAmountTuples = result.earnings_by_region.map(z => ({
    year: Number(z[0].slice(0, 4)),
    amount: getEarningAmount(z[1]),
  }));

  const yearTotalAmountTuples = Object.entries(
    groupBy(yearAmountTuples, t => t.year),
  ).map(([year, yearAmounts]) => ({
    year: Number(year),
    totalAmount: yearAmounts
      .map(z => z.amount)
      .reduce((prev, cur) => prev + cur, 0),
  }));

  return maxBy(yearTotalAmountTuples, t => t.totalAmount);
}

export function parseChartDataIntoTuples(
  earningsByQuarter: [string, EarningByRegion | number][],
): {
  date: Moment;
  amount: number | EarningByRegion;
}[] {
  return earningsByQuarter.map(z => ({
    date: moment(z[0]),
    amount: z[1],
  }));
}

export function getTopSongShare(result: ValuationResult): string {
  if (!result.top_tracks || !result.lte) {
    return "--";
  }

  const topsong = result.top_tracks[0];

  return formatMoney((topsong.earnings / result.lte) * 100, 0);
}

export function getAvgSongEarning(result: ValuationResult): string {
  const countOfSongsMakingMoney = result.tracks_with_earnings_count;
  return formatMoney(result.lte / countOfSongsMakingMoney, 0);
}

function translateName(
  n: string,
  translationMap: { [k: string]: string },
): string {
  if (n in translationMap) {
    return translationMap[n];
  }
  // The default translation for Source is capitalization (e.g.: 'SATELLITE RADIO' -> 'Satellite Radio')
  // If it is not enough, the explicit translation should be specified in backendSourceNameToFullName
  return titleize(n);
}

export function translateSourceName(n: string) {
  return translateName(n, earningSourceNameToDisplayName);
}

export function getReportOnTopSources(topSources: Source[]): Source[] {
  return topSources.every(s => !!s.earning_details)
    ? topSources.map(s => ({
        ...s,
        name: translateSourceName(s.name),
        earning_details: s.earning_details.map(d => ({
          ...d,
          name: translateSourceName(d.name),
        })),
      }))
    : topSources.map(s => ({
        ...s,
        name: translateSourceName(s.name),
      }));
}

export function getRegionsRate(
  result: ValuationResult,
): { label: string; value: string } {
  if (!valueIsDefined(result.regions)) {
    return null;
  }

  const nonDomesticTotal =
    result.regions.intl.lte + result.regions.unreported.lte;

  if (result.regions.domestic.lte > nonDomesticTotal) {
    if (nonDomesticTotal > 0) {
      const rate = result.regions.domestic.lte / nonDomesticTotal;
      return {
        label: "more royalties earned in the US than internationally",
        value: rate > 10 ? "> 10" : formatMoney(rate, 1),
      };
    } else {
      return {
        label: "more royalties earned in the US than internationally",
        value: "> 10",
      };
    }
  }
  if (result.regions.domestic.lte > 0) {
    const rate = nonDomesticTotal / result.regions.domestic.lte;
    return {
      label: "more royalties earned internationally than in the US",
      value: rate > 10 ? "> 10" : formatMoney(rate, 1),
    };
  } else {
    return {
      label: "more royalties earned internationally than in the US",
      value: "> 10",
    };
  }
}
